import zipWith from "lodash/zipWith";
import sum from "lodash/sum";
import flattenDeep from "lodash/flattenDeep";
import { parseSeries } from "views/logic/aggregationbuilder/Series";
import {
  getBaseUnit,
  getPrettifiedValue,
  roundValue,
  convertValueToBaseUnit
} from "views/components/visualizations/utils/unitConverters";
import {
  DEFAULT_AXIS_KEY,
  TIME_AXIS_LABELS_QUANTITY,
  DECIMAL_PLACES,
  NO_FIELD_NAME_SERIES,
  TICK_VALS_SECOND_MARGIN,
  TICK_VALS_FIRST_MARGIN
} from "views/components/visualizations/Constants";
import getFieldNameFromTrace from "views/components/visualizations/utils/getFieldNameFromTrace";
import getDefaultPlotYLayoutSettings from "views/components/visualizations/utils/getDefaultPlotYLayoutSettings";
import formatValueWithUnitLabel from "views/components/visualizations/utils/formatValueWithUnitLabel";
export const getTickLabelShift = (axisCount) => axisCount > 2 ? TICK_VALS_SECOND_MARGIN : TICK_VALS_FIRST_MARGIN;
export const getYAxisSide = (axisCount) => {
  if (axisCount % 2 === 0) {
    return "right";
  }
  return "left";
};
const getYAxisPositioningSettings = (axisCount) => ({
  side: getYAxisSide(axisCount),
  overlaying: axisCount > 1 ? "y" : void 0,
  automargin: true,
  autoshift: true,
  ticklabelposition: "outside",
  ticklabelstandoff: getTickLabelShift(axisCount)
});
const defaultSettings = {
  autoshift: true,
  automargin: true,
  fixedrange: true,
  rangemode: "tozero"
};
const getFormatSettingsWithCustomTickVals = (values, fieldType) => {
  const min = Math.min(0, ...values);
  const max = Math.max(...values);
  const step = (max - min) / TIME_AXIS_LABELS_QUANTITY;
  const valueBaseUnit = getBaseUnit(fieldType);
  const prettyRoundedValues = Array(TIME_AXIS_LABELS_QUANTITY).fill(null).map((_, index) => {
    const originalBaseValue = (index + 1) * step;
    const prettyValue = getPrettifiedValue(originalBaseValue, {
      abbrev: valueBaseUnit.abbrev,
      unitType: valueBaseUnit.unitType
    });
    return {
      value: roundValue(prettyValue.value),
      unit: prettyValue.unit
    };
  });
  const roundedTickValues = prettyRoundedValues.map(
    (prettified) => convertValueToBaseUnit(prettified.value, prettified.unit).value
  );
  const roundedTickText = prettyRoundedValues.map(
    (prettified) => formatValueWithUnitLabel(prettified == null ? void 0 : prettified.value, prettified.unit.abbrev, 0)
  );
  return {
    tickvals: roundedTickValues,
    ticktext: roundedTickText,
    range: [0, roundedTickValues[TIME_AXIS_LABELS_QUANTITY - 1]]
  };
};
export const getFormatSettingsByData = (unitTypeKey, values) => {
  switch (unitTypeKey) {
    case "percent":
      return {
        tickformat: `.${DECIMAL_PLACES}%`
      };
    case "size":
      return getFormatSettingsWithCustomTickVals(values, "size");
    case "time":
      return getFormatSettingsWithCustomTickVals(values, "time");
    default:
      return {
        tickformat: ",~r"
      };
  }
};
const getUnitLayoutWithData = (unitTypeKey, axisCount, values, theme, config) => ({
  ...getFormatSettingsByData(unitTypeKey, values),
  ...getYAxisPositioningSettings(axisCount),
  ...defaultSettings,
  ...getDefaultPlotYLayoutSettings(theme, unitTypeKey, config)
});
export const getBarChartTraceOffsetGroup = (barmode, yaxis, traceIndex) => {
  if (barmode === "stack" || barmode === "relative" || barmode === "overlay") {
    return yaxis;
  }
  if (barmode === "group") {
    return traceIndex;
  }
  return void 0;
};
export const generateMappersForYAxis = ({
  series,
  units
}) => {
  let axisCount = 1;
  const unitTypeMapper = {};
  const mapper = {};
  const mapperAxisNumber = {};
  const seriesUnitMapper = {};
  const fieldNameToAxisNameMapper = {};
  const fieldNameToAxisCountMapper = {};
  series.forEach((s) => {
    var _a, _b;
    const seriesName = s.config.name || s.function;
    const { field } = (_a = parseSeries(s.function)) != null ? _a : {};
    const unitType = (_b = units == null ? void 0 : units.getFieldUnit(field)) == null ? void 0 : _b.unitType;
    const unitTypeKey = unitType || DEFAULT_AXIS_KEY;
    const fieldKey = field != null ? field : NO_FIELD_NAME_SERIES;
    if (!unitTypeMapper[unitTypeKey]) {
      const axisNameNumberPart = axisCount > 1 ? axisCount : "";
      const axisKeyName = `yaxis${axisNameNumberPart}`;
      unitTypeMapper[unitTypeKey] = { axisCount, axisKeyName };
      mapper[seriesName] = `y${axisNameNumberPart}`;
      mapperAxisNumber[seriesName] = axisCount;
      fieldNameToAxisNameMapper[fieldKey] = `y${axisNameNumberPart}`;
      fieldNameToAxisCountMapper[fieldKey] = axisCount;
      axisCount += 1;
    } else {
      const currentAxisCount = unitTypeMapper[unitTypeKey].axisCount;
      const axisNameNumberPart = currentAxisCount > 1 ? currentAxisCount : "";
      mapper[seriesName] = `y${axisNameNumberPart}`;
      mapperAxisNumber[seriesName] = currentAxisCount;
      fieldNameToAxisNameMapper[fieldKey] = `y${axisNameNumberPart}`;
      fieldNameToAxisCountMapper[fieldKey] = currentAxisCount;
    }
    seriesUnitMapper[seriesName] = unitTypeKey;
  });
  return {
    unitTypeMapper,
    yAxisMapper: mapper,
    mapperAxisNumber,
    seriesUnitMapper,
    fieldNameToAxisNameMapper,
    fieldNameToAxisCountMapper
  };
};
const joinValues = (values = [], barmode) => {
  if (barmode === "stack" || barmode === "relative") {
    return zipWith(...values, (...iterateValues) => sum(iterateValues));
  }
  return flattenDeep(values);
};
export const generateLayouts = ({
  unitTypeMapper,
  chartData,
  barmode,
  widgetUnits,
  config,
  theme
}) => {
  const groupYValuesByUnitTypeKey = chartData.reduce(
    (res, value) => {
      var _a;
      const traceName = value.fullPath;
      const fieldName = getFieldNameFromTrace({ series: config.series, fullPath: traceName });
      const unit = widgetUnits.getFieldUnit(fieldName);
      const unitType = (_a = unit == null ? void 0 : unit.unitType) != null ? _a : DEFAULT_AXIS_KEY;
      if (!res[unitType]) {
        res[unitType] = [value.y];
      } else {
        res[unitType].push(value.y);
      }
      return res;
    },
    {}
  );
  return Object.fromEntries(
    Object.entries(unitTypeMapper).map(([unitTypeKey, { axisKeyName, axisCount }]) => {
      const unitValues = joinValues(groupYValuesByUnitTypeKey[unitTypeKey], barmode);
      return [axisKeyName, getUnitLayoutWithData(unitTypeKey, axisCount, unitValues, theme, config)];
    })
  );
};
const getHoverTexts = ({ convertedValues, unit }) => convertedValues.map((value) => {
  const prettified = unit && unit.isDefined && getPrettifiedValue(value, {
    abbrev: unit.abbrev,
    unitType: unit.unitType
  });
  if (!prettified) return value;
  return formatValueWithUnitLabel(prettified == null ? void 0 : prettified.value, prettified.unit.abbrev);
});
export const getHoverTemplateSettings = ({
  convertedValues,
  unit,
  name = void 0
}) => {
  if ((unit == null ? void 0 : unit.unitType) === "time" || (unit == null ? void 0 : unit.unitType) === "size") {
    return {
      text: getHoverTexts({ convertedValues, unit }),
      hovertemplate: `%{text}<br>${name ? "<extra>%{meta}</extra>" : "<extra></extra>"}`,
      meta: name
    };
  }
  return {};
};
export const getPieHoverTemplateSettings = ({
  convertedValues,
  unit,
  name
}) => ({
  text: getHoverTexts({ convertedValues, unit }),
  hovertemplate: "<b>%{label}</b><br>%{text}<br>%{percent}",
  meta: name,
  textinfo: "percent"
});
